/* Warehouse Images — central library of internal product images.
 *
 * Architecture:
 *   - One MASTER image per Shopify product (stored on `warehouse_products`).
 *   - Tasks SNAPSHOT this image at the moment of creation. Editing the master
 *     later does NOT change existing tasks.
 *   - Goal: pickers can recognise items by photo alone (no text, no SKU, no
 *     Romanian).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  listWarehouseImages, uploadWarehouseImage, deleteWarehouseImage,
  setProductOverride, managerImageUrl,
} from "@/lib/warehouse";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Image as ImageIcon, Upload, Search, Loader2, CheckCircle2, AlertCircle, Trash2, Pencil, X, ArrowLeft,
} from "lucide-react";

export default function WarehouseImagesPage() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(params.get("q") || "");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listWarehouseImages({ q: search, only_missing: onlyMissing, limit: 150 });
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [search, onlyMissing]);

  // Debounce search reload.
  useEffect(() => {
    const t = setTimeout(reload, 280);
    return () => clearTimeout(t);
  }, [reload]);

  const stats = useMemo(() => {
    const withImg = items.filter((i) => i.has_warehouse_image).length;
    return { withImg, without: items.length - withImg };
  }, [items]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="warehouse-images-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <Link
            to="/coletare"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2"
            data-testid="wi-back-to-tasks"
          >
            <ArrowLeft className="h-3 w-3" /> Înapoi la Task-uri
          </Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ImageIcon className="h-6 w-6 text-primary" /> Imagini depozit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Bibliotecă internă de fotografii. Pickerii recunosc produsul doar din imagine.
            <span className="ml-2 inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {stats.withImg} cu imagine</span>
            <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><AlertCircle className="h-3.5 w-3.5" /> {stats.without} fără imagine</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="wi-filter-only-missing"
            onClick={() => setOnlyMissing((v) => !v)}
            className={`px-3 py-1.5 rounded-md text-xs border transition ${
              onlyMissing ? "bg-amber-500 text-white border-amber-600" : "bg-card hover:bg-accent border-border"
            }`}
          >
            {onlyMissing ? "Arăt doar fără imagine ✓" : "Doar fără imagine"}
          </button>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="wi-search"
              className="pl-8 w-72"
              placeholder="Caută produs (titlu, SKU, vendor)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="py-32 text-center text-sm text-muted-foreground">Niciun produs.</div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
          {items.map((it) => (
            <ProductCard key={it.product_id} item={it} onOpen={() => setEditTarget(it)} onChanged={reload} />
          ))}
        </div>
      )}

      {editTarget && (
        <EditImageDialog
          item={editTarget}
          open={!!editTarget}
          onOpenChange={(v) => !v && setEditTarget(null)}
          onSaved={() => { reload(); setEditTarget(null); }}
        />
      )}

      {total > items.length && (
        <div className="text-center py-6 text-xs text-muted-foreground">
          Afișez {items.length} din {total}. Filtrează ca să restrângi lista.
        </div>
      )}
    </div>
  );
}

function ProductCard({ item, onOpen, onChanged }) {
  const has = item.has_warehouse_image;
  const wImg = item.warehouse_image_url ? managerImageUrl(item.warehouse_image_url) : null;
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    try {
      await uploadWarehouseImage(item.product_id, file);
      toast.success("Imagine depozit salvată");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div
      data-testid={`wi-card-${item.product_id}`}
      className={`group rounded-xl border bg-card overflow-hidden flex flex-col transition ${
        dragOver ? "border-primary ring-2 ring-primary/30" : has ? "border-emerald-200 dark:border-emerald-900" : "border-amber-300 dark:border-amber-900"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      {/* Warehouse image — the main slot */}
      <button
        onClick={() => (has ? onOpen() : inputRef.current?.click())}
        className="relative aspect-square bg-muted overflow-hidden block w-full text-left"
        title={has ? "Editează" : "Adaugă imagine depozit"}
      >
        {wImg ? (
          <img src={wImg} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">
            <Upload className="h-8 w-8" />
            <span className="text-xs font-medium text-center px-2">Trage o imagine aici<br/>sau click pentru a alege</span>
          </div>
        )}
        {has && (
          <div className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
            DEPOZIT
          </div>
        )}
      </button>

      {/* Small Shopify image preview */}
      {item.shopify_image_url && (
        <div className="px-3 py-2 flex items-center gap-2 border-t border-border bg-muted/30">
          <img src={item.shopify_image_url} alt="" className="h-8 w-8 object-cover rounded border border-border" />
          <span className="text-[10px] text-muted-foreground">Shopify (referință)</span>
        </div>
      )}

      {/* Title + label */}
      <div className="p-3 flex-1 flex flex-col gap-1">
        <div className="text-sm font-medium line-clamp-2 leading-snug" title={item.title}>{item.title}</div>
        {item.warehouse_label && (
          <Badge variant="outline" className="self-start text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">
            {item.warehouse_label}
          </Badge>
        )}
        <div className="text-[10px] text-muted-foreground font-mono mt-auto">{item.sku || item.vendor || item.product_id}</div>
      </div>

      {/* Actions */}
      <div className="px-3 pb-3 flex gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`wi-upload-${item.product_id}`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-8 text-xs"
          onClick={() => inputRef.current?.click()}
          data-testid={`wi-upload-btn-${item.product_id}`}
        >
          <Upload className="h-3.5 w-3.5 mr-1" /> {has ? "Înlocuiește" : "Încarcă"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2"
          onClick={onOpen}
          title="Editează detalii"
          data-testid={`wi-edit-${item.product_id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function EditImageDialog({ item, open, onOpenChange, onSaved }) {
  const [label, setLabel] = useState(item.warehouse_label || "");
  const [notes, setNotes] = useState(item.warehouse_notes || "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const wImg = item.warehouse_image_url ? managerImageUrl(item.warehouse_image_url) : null;

  const save = async () => {
    setBusy(true);
    try {
      await setProductOverride(item.product_id, { warehouse_label: label || null, warehouse_notes: notes || null });
      toast.success("Salvat");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const replace = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await uploadWarehouseImage(item.product_id, file);
      toast.success("Imagine actualizată");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Sigur ștergi imaginea depozit? Task-urile existente păstrează snapshot-ul lor.")) return;
    setBusy(true);
    try {
      await deleteWarehouseImage(item.product_id);
      toast.success("Imagine ștearsă");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="wi-edit-dialog">
        <DialogHeader>
          <DialogTitle className="line-clamp-2">{item.title}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{item.sku || item.vendor || item.product_id}</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Image */}
          <div>
            <div className="aspect-square bg-muted border border-border rounded-md overflow-hidden relative group">
              {wImg ? (
                <img src={wImg} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><ImageIcon className="h-12 w-12" /></div>
              )}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => replace(e.target.files?.[0])}
              data-testid="wi-dialog-upload"
            />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" className="flex-1" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="wi-dialog-replace">
                <Upload className="h-4 w-4 mr-1" /> {wImg ? "Înlocuiește" : "Încarcă"}
              </Button>
              {wImg && (
                <Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={remove} disabled={busy} data-testid="wi-dialog-delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            {item.shopify_image_url && (
              <div className="mt-3 p-2 rounded border border-border bg-muted/30">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Shopify (referință)</div>
                <img src={item.shopify_image_url} alt="" className="w-full max-h-32 object-contain" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Etichetă scurtă</label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="ex: CUTIE VERDE"
                data-testid="wi-dialog-label"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Apare pe cardul TV deasupra titlului.</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">Note pentru picker</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="ex: se pune cu accesoriile în punga separată"
                data-testid="wi-dialog-notes"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}><X className="h-4 w-4 mr-1" /> Renunță</Button>
          <Button onClick={save} disabled={busy} data-testid="wi-dialog-save">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
